/**
 * The Staff App's offline queue — a thin policy layer over
 * `@sonauto/offline-sync`.
 *
 * The shared package owns the IndexedDB store, the FIFO replay with
 * exponential backoff, the PENDING/SENDING/SYNCED/FAILED/CONFLICT lifecycle and
 * the allow-list of queueable operations (`assertQueueable` THROWS on anything
 * else). This file owns three things it cannot know:
 *
 *   1. WHICH staff operations this app queues, and why not the others.
 *   2. The transport — how a queued record becomes an API call.
 *   3. The Vietnamese labels the employee reads in the "chưa đồng bộ" list.
 *
 * WHAT THIS APP QUEUES, AND WHAT IT DELIBERATELY DOES NOT
 * -------------------------------------------------------
 * QUEUED — `ATTENDANCE_CHECK_IN`, `ATTENDANCE_CHECK_OUT`, `TASK_COMPLETE`:
 *   an employee arriving at a basement staff entrance with no signal must
 *   still be able to clock in, and a walk-in fridge is the most common place a
 *   task is finished out of coverage. Each carries a client UUID that is also
 *   the idempotency key, and the SERVER stamps its own clock on arrival — so a
 *   queued check-in is evidence of intent, never a claim about time. The UI
 *   says so out loud.
 *
 * NOT QUEUED — `TASK_ACCEPT`, even though the shared allow-list permits it:
 *   accepting is a compare-and-set race on `tasks.claim_version` against an
 *   offer with a server-side deadline. Replaying an accept minutes later either
 *   loses the race (confusing) or claims a task somebody else already
 *   completed. The offer is gone by then anyway. The Công việc screen disables
 *   the button offline and explains why, which is honest; queuing it would be a
 *   promise the product cannot keep.
 *
 * NOT QUEUED — QR scans: a scan is evidence that a person was at a place at a
 *   time, and replaying one from somewhere else destroys exactly the property
 *   it exists to prove.
 *
 * NOT QUEUED — leave, swaps, advances, appeals: approval workflows with a human
 *   on the other end. Silently queuing one would let an employee believe a
 *   leave request was filed when it was not.
 */
import {
  deriveSyncState,
  discard,
  enqueue,
  lastSyncedAt,
  listAll,
  replay,
  retryNow,
  type OutboxKind,
  type OutboxRecord,
  type ReplayOutcome,
} from '@sonauto/offline-sync';
import { ApiError } from '@sonauto/api-client';
import { apiClient } from './api';
import type { AttendanceRequest, TaskCompleteRequest } from './contracts';
import { publishOutbox } from './sync-status';

export type { OutboxRecord } from '@sonauto/offline-sync';

// ---------------------------------------------------------------------------
// Enqueue
// ---------------------------------------------------------------------------

/**
 * Queue an attendance event.
 *
 * The caller has already generated `clientEventId`; it becomes the record id
 * AND the `Idempotency-Key`, so however many times the record is replayed the
 * server's unique index on `attendance_events (tenant_id, idempotency_key)`
 * sees one physical tap.
 */
export async function enqueueAttendance(
  request: AttendanceRequest,
  label: string,
): Promise<OutboxRecord> {
  const kind: OutboxKind = request.actionType === 'CHECK_OUT' ? 'ATTENDANCE_CHECK_OUT' : 'ATTENDANCE_CHECK_IN';
  const record = await enqueue({
    kind,
    label,
    id: request.clientEventId,
    idempotencyKey: request.clientEventId,
    request: {
      method: 'POST',
      path: '/v1/staff/attendance',
      // `offline: true` makes the server store `offline_status =
      // OFFLINE_SYNCED`, which is what a manager reviewing it sees.
      body: { ...request, offline: true },
    },
  });
  await refresh();
  return record;
}

export async function enqueueTaskComplete(
  taskId: string,
  taskTitle: string,
  request: TaskCompleteRequest,
): Promise<OutboxRecord> {
  const record = await enqueue({
    kind: 'TASK_COMPLETE',
    label: `Hoàn thành: ${taskTitle}`,
    request: {
      method: 'POST',
      path: `/v1/tasks/${encodeURIComponent(taskId)}/complete`,
      body: request,
    },
  });
  await refresh();
  return record;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/**
 * Turn one queued record into an API call.
 *
 * `DUPLICATE_REQUEST` is reported as SUCCESS: it is the server telling us the
 * original attempt landed after all, which is precisely the outcome the
 * idempotency key exists to produce. Treating it as an error would leave a
 * successfully-recorded check-in sitting in the queue forever.
 */
const transport = async (record: OutboxRecord): Promise<ReplayOutcome> => {
  try {
    await apiClient.request(record.request.method, record.request.path, {
      body: record.request.body,
      // The SAME key as the first attempt — that is the whole point.
      idempotencyKey: record.idempotencyKey,
      reason: record.request.reason ?? null,
    });
    return { ok: true };
  } catch (error) {
    if (!(error instanceof ApiError)) {
      return { ok: false, retryable: true, status: null, message: 'Lỗi không xác định khi gửi lại.' };
    }
    if (error.code === 'DUPLICATE_REQUEST') return { ok: true };
    return {
      ok: false,
      // 4xx cannot be fixed by retrying; 429 and 5xx and connectivity can.
      retryable: error.retryable || error.status === 0 || error.status >= 500,
      status: error.status,
      message: error.message,
    };
  }
};

// ---------------------------------------------------------------------------
// Snapshot + subscription
// ---------------------------------------------------------------------------

type Listener = (records: readonly OutboxRecord[]) => void;
const listeners = new Set<Listener>();
let snapshotRecords: readonly OutboxRecord[] = [];

export function subscribeOutbox(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function outboxSnapshot(): readonly OutboxRecord[] {
  return snapshotRecords;
}

/** Rows the employee still has to care about — SYNCED ones are history. */
export function isOutstanding(record: OutboxRecord): boolean {
  return record.status !== 'SYNCED';
}

async function refresh(): Promise<void> {
  let all: OutboxRecord[] = [];
  try {
    all = await listAll();
  } catch {
    // IndexedDB unavailable (private browsing on some devices). The app still
    // works online; it simply cannot queue.
    all = [];
  }

  snapshotRecords = all
    .filter(isOutstanding)
    .sort((a, b) => a.createdAt - b.createdAt);

  const sending = snapshotRecords.filter((r) => r.status === 'SENDING').length;
  const pending = snapshotRecords.filter((r) => r.status === 'PENDING').length + sending;
  const failed = snapshotRecords.filter((r) => r.status === 'FAILED').length;
  const conflicts = snapshotRecords.filter((r) => r.status === 'CONFLICT').length;
  const online = typeof navigator === 'undefined' ? true : navigator.onLine;

  publishOutbox({
    pending,
    failed,
    conflicts,
    lastSyncedAt: await lastSyncedAt().catch(() => null),
    // The shared precedence rule: a conflict outranks everything, because only
    // a human can clear it.
    state: deriveSyncState({
      online,
      pendingCount: pending - sending,
      sendingCount: sending,
      failedCount: failed,
      conflictCount: conflicts,
    }),
  });

  for (const listener of listeners) listener(snapshotRecords);
}

// ---------------------------------------------------------------------------
// Replay
// ---------------------------------------------------------------------------

let draining = false;

export async function drainOutbox(): Promise<void> {
  if (draining) return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;

  draining = true;
  await refresh();
  try {
    await replay(transport);
  } finally {
    draining = false;
    await refresh();
  }
}

/** Move a FAILED/CONFLICT row back to PENDING and try it immediately. */
export async function retryEntry(id: string): Promise<void> {
  await retryNow(id);
  await refresh();
  void drainOutbox();
}

/** User-initiated removal of a row that will never succeed. */
export async function discardEntry(id: string): Promise<void> {
  await discard(id);
  await refresh();
}

/** Wired once from main.tsx: drain on boot and whenever the radio comes back. */
export function installOutboxReplay(): void {
  void refresh().then(() => drainOutbox());
  if (typeof window === 'undefined') return;
  window.addEventListener('online', () => {
    void drainOutbox();
  });
  // Returning from the background is the other moment connectivity typically
  // comes back without an `online` event firing.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void drainOutbox();
  });
}
